import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import type { AiLogItem, AiLogTool } from '../../../types/aiLog';
import { formatDateTime } from '../../../utils/dateFormat';
import {
  formatCredits,
  formatLatency,
  formatUsd,
  formatVarianceLabel,
  latencySourceLabel,
  originLabel,
  varianceColor,
  varianceTooltip,
} from './aiLogFormat';

const toolColor = (tool: AiLogTool): 'default' | 'success' | 'error' => {
  if (tool.success === true) return 'success';
  if (tool.success === false) return 'error';
  return 'default';
};

const renderToolChip = (tool: AiLogTool) => (
  <Tooltip
    key={tool.name}
    title={
      tool.executionTimeMs === null || tool.executionTimeMs === undefined
        ? tool.name
        : `${tool.name} · ${formatLatency(tool.executionTimeMs)}`
    }
  >
    <Chip size="small" color={toolColor(tool)} variant="outlined" label={tool.name} />
  </Tooltip>
);

const copyLogId = (event: React.MouseEvent, logId: string) => {
  event.stopPropagation();
  void navigator.clipboard?.writeText(logId).catch(() => undefined);
};

const tokenCell = (value: number | null, suffix?: React.ReactNode) => (
  <Box className="flex items-center gap-1">
    <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
      {formatCredits(value)}
    </Typography>
    {suffix}
  </Box>
);

export const createAiLogsColumns = (): GridColDef<AiLogItem>[] => [
  {
    field: 'createdAt',
    headerName: 'Time',
    width: 170,
    renderCell: ({ row }) => (
      <Tooltip
        title={row.status === 'failed' ? 'Failed call; open detail for error information' : ''}
      >
        <Box
          className={
            row.status === 'failed'
              ? 'w-full rounded bg-red-50 px-2 py-1 dark:bg-red-950/30'
              : 'w-full px-2 py-1'
          }
        >
          <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
            {formatDateTime(row.createdAt, true)}
          </Typography>
          {row.status !== 'success' && (
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {row.status}
            </Typography>
          )}
        </Box>
      </Tooltip>
    ),
  },
  {
    field: 'origin',
    headerName: 'Source',
    width: 150,
    renderCell: ({ row }) => (
      <Box>
        <Chip size="small" variant="outlined" label={originLabel(row.origin)} />
        {row.origin === 'api_key' && row.apiKey?.prefix && (
          <Typography variant="caption" className="mt-1 block text-gray-500 dark:text-slate-400">
            {row.apiKey.prefix}
          </Typography>
        )}
      </Box>
    ),
  },
  {
    field: 'user',
    headerName: 'User',
    minWidth: 210,
    flex: 1,
    renderCell: ({ row }) =>
      row.user ? (
        <Box className="min-w-0">
          <Typography variant="body2" className="truncate text-gray-900 dark:text-slate-100">
            {row.user.email}
          </Typography>
          {row.user.username && (
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {row.user.username}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
          —
        </Typography>
      ),
  },
  {
    field: 'model',
    headerName: 'Model',
    minWidth: 180,
    flex: 1,
    renderCell: ({ row }) => (
      <Box className="min-w-0">
        <Typography variant="body2" className="truncate text-gray-900 dark:text-slate-100">
          {row.model}
        </Typography>
        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
          {row.providerCode || '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'services',
    headerName: 'Services',
    minWidth: 230,
    flex: 1,
    renderCell: ({ row }) => (
      <Box className="min-w-0">
        <Box className="flex flex-wrap gap-1">
          <Chip size="small" label={row.services.capabilityCode} />
          {row.services.internalTaskKey && (
            <Chip
              size="small"
              variant="outlined"
              label={`internal: ${row.services.internalTaskKey}`}
            />
          )}
        </Box>
        <Typography variant="caption" className="truncate text-gray-500 dark:text-slate-400">
          {row.services.source} · {row.services.entrypoint}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'tools',
    headerName: 'Tools',
    minWidth: 220,
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const visibleTools = row.tools.slice(0, 3);
      const hiddenTools = row.tools.slice(3);
      return row.tools.length ? (
        <Box className="flex flex-wrap gap-1">
          {visibleTools.map(renderToolChip)}
          {hiddenTools.length > 0 && (
            <Tooltip title={hiddenTools.map((tool) => tool.name).join(', ')}>
              <Chip size="small" variant="outlined" label={`+${hiddenTools.length}`} />
            </Tooltip>
          )}
        </Box>
      ) : (
        <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
          —
        </Typography>
      );
    },
  },
  {
    field: 'latencyMs',
    headerName: 'Latency',
    width: 130,
    renderCell: ({ row }) => (
      <Tooltip title={latencySourceLabel(row.latencySource)}>
        <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
          {formatLatency(row.latencyMs)}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'gatewayLogId',
    headerName: 'Log ID',
    width: 170,
    sortable: false,
    renderCell: ({ row }) =>
      row.gatewayLogId ? (
        <Tooltip title={`${row.gatewayLogId} (click to copy)`}>
          <Typography
            variant="body2"
            className="cursor-copy truncate font-mono text-gray-900 dark:text-slate-100"
            onClick={(event) => copyLogId(event, row.gatewayLogId as string)}
          >
            {row.gatewayLogId}
          </Typography>
        </Tooltip>
      ) : (
        <Tooltip title="No Cloudflare AI Gateway log id was captured for this call">
          <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
            —
          </Typography>
        </Tooltip>
      ),
  },
  {
    field: 'estimatedPromptTokens',
    headerName: 'Est. input tokens',
    width: 150,
    sortable: false,
    renderCell: ({ row }) => (
      <Tooltip title="Pre-call estimate of input tokens only. Output tokens are unknown until the model answers, so they are not estimated here.">
        <span>{tokenCell(row.usage.estimatedPromptTokens)}</span>
      </Tooltip>
    ),
  },
  {
    field: 'actualPromptTokens',
    headerName: 'Actual input tokens',
    width: 150,
    sortable: false,
    renderCell: ({ row }) => tokenCell(row.usage.promptTokens),
  },
  {
    field: 'actualTokens',
    headerName: 'Total tokens (in + out)',
    width: 150,
    sortable: false,
    renderCell: ({ row }) => (
      <Box className="flex flex-col justify-center leading-tight">
        <Box className="flex items-center gap-1">
          <Typography variant="body2" className="font-semibold text-gray-900 dark:text-slate-100">
            {formatCredits(row.usage.totalTokens)}
          </Typography>
          {row.usage.usageEstimated ? (
            <Tooltip title="Provider reported no usage; this figure is our own estimate">
              <Chip size="small" variant="outlined" label="est." />
            </Tooltip>
          ) : null}
        </Box>
        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
          {formatCredits(row.usage.promptTokens)} + {formatCredits(row.usage.completionTokens)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'shadowCredits',
    headerName: 'Credits charged',
    width: 150,
    renderCell: ({ row }) => (
      <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
        {formatCredits(row.cost.shadowCredits)}
      </Typography>
    ),
  },
  {
    field: 'gatewayCostUsd',
    headerName: 'Cloudflare cost',
    width: 150,
    renderCell: ({ row }) => (
      <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
        {row.cost.gatewayCostUsd === null ? 'pending' : formatUsd(row.cost.gatewayCostUsd)}
      </Typography>
    ),
  },
  {
    field: 'variance',
    headerName: 'Variance',
    width: 130,
    renderCell: ({ row }) => (
      <Tooltip title={varianceTooltip(row.cost.variancePercent, row.cost.outOfTolerance)}>
        <Chip
          size="small"
          color={varianceColor(row.cost.variancePercent, row.cost.outOfTolerance)}
          variant={row.cost.variancePercent === null ? 'outlined' : 'filled'}
          label={formatVarianceLabel(row.cost.variancePercent)}
        />
      </Tooltip>
    ),
  },
];
